import { useState } from 'react'
import { useTemplates } from '../../api/templates'
import { useGeometryStore } from '../../store/geometryStore'
import type { TemplateJson } from '../../types/template'
import { ddToMgrs, formatMgrs } from '../../utils/geo/mgrs'
import { formatDDPair } from '../../utils/geo/formats'

interface Props {
  onSelect: (templateName: string) => void
}

function getCompatibilityScore(template: { meta: TemplateJson['meta'] }, geomType: string): 'full' | 'partial' | 'none' {
  // Simple heuristic based on template tags
  const tags = template.meta.tags ?? []
  if (geomType === 'point' && tags.some((t) => t.includes('point'))) return 'full'
  if (geomType === 'polygon' && tags.some((t) => t.includes('polygon') || t.includes('area'))) return 'full'
  if (tags.some((t) => t.includes('generic') || t.includes('geo'))) return 'partial'
  return 'none'
}

export default function TemplateSelector({ onSelect }: Props) {
  const { data: templates, isLoading } = useTemplates()
  const { drawnGeometry } = useGeometryStore()
  const [selected, setSelected] = useState<string | null>(null)

  if (!drawnGeometry) return null

  const { centroid, areaKm2, mode } = drawnGeometry
  const [lon, lat] = centroid // [lon, lat] - GeoJSON order
  const mgrsRaw = ddToMgrs(lat, lon)
  const mgrs = mgrsRaw ? formatMgrs(mgrsRaw) : 'N/A'

  return (
    <div className="space-y-6">
      {/* Geometry summary */}
      <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-4 space-y-2">
        <h3 className="text-sm font-semibold text-blue-400">Drawn Geometry</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
          <span className="text-muted-foreground">Type:</span>
          <span className="capitalize">{mode}</span>
          <span className="text-muted-foreground">Centroid:</span>
          <span>{formatDDPair(lat, lon)}</span>
          <span className="text-muted-foreground">MGRS:</span>
          <span>{mgrs}</span>
          {areaKm2 !== undefined && areaKm2 > 0 && (
            <>
              <span className="text-muted-foreground">Area:</span>
              <span>{areaKm2.toFixed(3)} km²</span>
            </>
          )}
        </div>
      </div>

      {/* Template grid */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Select Template</h3>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {templates?.map((t) => {
              const compat = getCompatibilityScore(t, mode)
              return (
                <button
                  key={t.name}
                  onClick={() => setSelected(t.name)}
                  className={`text-left rounded-lg border p-3 transition-all
                    ${selected === t.name
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-border hover:border-blue-500/50 hover:bg-accent/50'
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium leading-tight">{t.meta.name}</span>
                    {compat !== 'none' && (
                      <span className={`shrink-0 text-xs rounded px-1.5 py-0.5
                        ${compat === 'full'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                        {compat === 'full' ? 'Match' : 'Compatible'}
                      </span>
                    )}
                  </div>
                  {t.meta.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {t.meta.description}
                    </p>
                  )}
                  {t.meta.classification && (
                    <span className="text-xs font-mono text-yellow-400/70 mt-1 block">
                      {t.meta.classification}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {selected && (
        <button
          onClick={() => onSelect(selected)}
          className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Fill Template →
        </button>
      )}
    </div>
  )
}
